import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const PricingContext = createContext();

export const usePricing = () => {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error('usePricing must be used within PricingProvider');
  }
  return context;
};

export const PricingProvider = ({ children }) => {
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPricingData();
  }, []);

  const fetchPricingData = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://trendvector-backend.onrender.com';
      
      // Add timestamp to URL to force cache busting at all levels (browser, CDN, proxy)
      const timestamp = new Date().getTime();
      const url = `${API_BASE_URL}/api/v1/public/pricing-details?_t=${timestamp}`;
      
      console.log('📊 Fetching pricing data from:', url);
      
      const response = await axios.get(url, {
        // Prevent caching to always get fresh data
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        // Axios-specific cache bypass
        params: {
          _nocache: timestamp
        }
      });
      
      console.log('✅ Pricing data received:', response.data);
      
      // Validate that we received valid data
      if (response.data && response.data.plans && response.data.plans.length > 0) {
        setPricingData(response.data);
        setError(null);
        console.log('✅ Pricing data successfully loaded with', response.data.plans.length, 'plans');
        
        // Log each plan's price for verification
        response.data.plans.forEach(plan => {
          console.log(`💰 ${plan.name}: ₹${plan.original_price}${plan.promo_price ? ` (Promo: ₹${plan.promo_price})` : ''}`);
        });
      } else {
        throw new Error('Invalid pricing data received from API');
      }
    } catch (err) {
      console.error('❌ Error fetching pricing data:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.message);
      
      // Fallback to default pricing if API fails
      // WARNING: These are emergency fallback values only. Normally, prices come from database via API.
      // Last updated: Check admin panel for current prices.
      console.warn('⚠️ Using fallback pricing data due to API failure. Prices may be outdated!');
      setPricingData({
        active_offer: null,
        plans: [
          {
            name: 'BASIC',
            display_name: 'TrendVector Basic',
            original_price: 499,  // Updated to match current database
            promo_price: null,
            features: [
              'Long-Term Model',
              'Email Support'
            ],
            sort_order: 1
          },
          {
            name: 'VECTOR PRO',
            display_name: 'Vector Pro',
            original_price: 999,
            promo_price: null,
            features: [
              'All BASIC',
              'Short-Term Model'
            ],
            sort_order: 2
          },
          {
            name: 'EXPERT',
            display_name: 'Expert Plan',
            original_price: 2999,
            promo_price: null,
            features: [
              'All PRO',
              'AI Analyst'
            ],
            sort_order: 3
          }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const value = {
    activeOffer: pricingData?.active_offer || null,
    plans: pricingData?.plans || [],
    loading,
    error,
    refreshPricing: fetchPricingData
  };

  return (
    <PricingContext.Provider value={value}>
      {children}
    </PricingContext.Provider>
  );
};

